import * as openai from "openai";
import * as Core from "openai/core";
import { readEnv } from "openai/core";
import type {
  ChatCompletion,
  ChatCompletionChunk,
  ChatCompletionCreateParams,
  ChatCompletionCreateParamsBase,
  ChatCompletionCreateParamsNonStreaming,
  ChatCompletionCreateParamsStreaming,
} from "openai/resources/chat/completions";

import { WrappedStream } from "./openai/streaming";
import { ApiError, DefaultService } from "./codegen";
import type { Stream } from "openai/streaming";
import {
  OpenPipeArgs,
  OpenPipeMeta,
  type OpenPipeConfig,
  getTags,
  OpenPipeChatCompletion,
} from "./shared";
import OpenPipe from "./client";
import { OpenAIError } from "openai";
import { WrappedBeta } from "./openaiBeta";

const MISSING_OPENAI_API_KEY = "MISSING_OPENAI_API_KEY";

export type ClientOptions = openai.ClientOptions & { openpipe?: OpenPipeConfig | OpenPipe };

export default class OpenAI extends openai.OpenAI {
  constructor({ openpipe, ...options }: ClientOptions = {}) {
    super({
      ...options,
      apiKey: options.apiKey || readEnv("OPENAI_API_KEY") || MISSING_OPENAI_API_KEY,
    });
    const openpipeClient = openpipe instanceof OpenPipe ? openpipe : new OpenPipe(openpipe);
    const openPipeApiKey = openpipeClient.baseClient.request.config.TOKEN;
    const fallbackClient =
      openpipe && "fallbackClient" in openpipe && openpipe.fallbackClient instanceof openai.OpenAI
        ? openpipe.fallbackClient
        : new openai.OpenAI({ ...options, apiKey: this.apiKey });

    if (typeof openPipeApiKey === "string" && openPipeApiKey.length > 0) {
      this.chat.setClients(
        openpipeClient,
        new openai.OpenAI({
          ...options,
          baseURL: openpipeClient.baseClient.request.config.BASE,
          apiKey: openPipeApiKey,
        }),
        fallbackClient,
      );
    } else {
      console.warn(
        "You're using the OpenPipe client without an API key. No completion requests will be logged.",
      );
    }
  }
  chat: WrappedChat = new WrappedChat(this);
  beta: WrappedBeta = new WrappedBeta(this);
}

class WrappedChat extends openai.OpenAI.Chat {
  completions: WrappedCompletions = new WrappedCompletions(this._client);

  setClients(opClient: OpenPipe, opCompletionClient: openai.OpenAI, fallbackClient: openai.OpenAI) {
    this.completions.opClient = opClient;
    this.completions.opCompletionClient = opCompletionClient;
    this.completions.fallbackClient = fallbackClient;
  }
}

class WrappedCompletions extends openai.OpenAI.Chat.Completions {
  // keep a reference to the original client so we can read options from it
  openaiClient: openai.OpenAI;
  opClient?: OpenPipe;
  opCompletionClient?: openai.OpenAI;
  fallbackClient: openai.OpenAI;

  constructor(client: openai.OpenAI) {
    super(client);
    this.openaiClient = client;
    this.fallbackClient = client;
  }

  async _report(args: Parameters<DefaultService["report"]>[0]) {
    try {
      this.opClient ? await this.opClient.report(args) : Promise.resolve();
    } catch (e) {
      // Ignore errors with reporting
    }
  }

  async _handleResponse(
    response: ChatCompletion | Stream<ChatCompletionChunk>,
    usedBody: ChatCompletionCreateParams,
    openpipeArgs: OpenPipeArgs,
    requestedAt: number,
  ) {
    let reportingFinished: OpenPipeMeta["reportingFinished"] = Promise.resolve();
    if (usedBody.stream) {
      try {
        return new WrappedStream(response as Stream<ChatCompletionChunk>, (response) => {
          if (!openpipeArgs.openpipe?.logRequest) return Promise.resolve();
          return this._report({
            requestedAt,
            receivedAt: Date.now(),
            reqPayload: usedBody,
            respPayload: response,
            statusCode: 200,
            tags: getTags(openpipeArgs.openpipe),
          });
        });
      } catch (e) {
        console.error("OpenPipe: error creating wrapped stream");
        console.error(e);
        throw e;
      }
    } else {
      reportingFinished = openpipeArgs.openpipe?.logRequest
        ? this._report({
            requestedAt,
            receivedAt: Date.now(),
            reqPayload: usedBody,
            respPayload: response,
            statusCode: 200,
            tags: getTags(openpipeArgs.openpipe),
          })
        : Promise.resolve();
      return {
        ...(response as ChatCompletion),
        openpipe: {
          reportingFinished,
        },
      };
    }
  }

  async _handleResponseError(
    error: unknown,
    usedBody: ChatCompletionCreateParams,
    openpipeArgs: OpenPipeArgs,
    requestedAt: number,
  ) {
    let reportingFinished: OpenPipeMeta["reportingFinished"] = Promise.resolve();

    if (error instanceof openai.APIError || error instanceof ApiError) {
      const rawMessage =
        error instanceof openai.APIError
          ? (error.message as string | string[])
          : error.body.message;
      const message = Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage;

      reportingFinished = this._report({
        requestedAt,
        receivedAt: Date.now(),
        reqPayload: usedBody,
        respPayload: error instanceof openai.APIError ? error.error : error.body,
        statusCode: error.status,
        errorMessage: message,
        tags: getTags(openpipeArgs.openpipe),
      });
    }

    if (error !== null) {
      (error as { openpipe?: any }).openpipe = {
        reportingFinished,
      };
    }

    return error;
  }
  // @ts-expect-error It doesn't like the fact that I added a `Promise<>`
  // wrapper but I actually think the types are correct here.
  create(
    body: ChatCompletionCreateParamsNonStreaming & OpenPipeArgs,
    options?: Core.RequestOptions,
  ): Core.APIPromise<OpenPipeChatCompletion & { openpipe?: OpenPipeMeta }>;
  create(
    body: ChatCompletionCreateParamsStreaming & OpenPipeArgs,
    options?: Core.RequestOptions,
  ): Core.APIPromise<Stream<ChatCompletionChunk> & { openpipe?: OpenPipeMeta }>;
  create(
    body: ChatCompletionCreateParamsBase & OpenPipeArgs,
    options?: Core.RequestOptions,
  ): Core.APIPromise<Stream<ChatCompletionChunk> | OpenPipeChatCompletion>;
  async create(
    { openpipe: rawOpenpipe, ...body }: ChatCompletionCreateParams & OpenPipeArgs,
    options?: Core.RequestOptions,
  ): Promise<
    Core.APIPromise<
      | (OpenPipeChatCompletion & { openpipe?: OpenPipeMeta })
      | (Stream<ChatCompletionChunk> & { openpipe?: OpenPipeMeta })
    >
  > {
    const openpipe = { logRequest: true, ...rawOpenpipe };
    const requestedAt = Date.now();

    if (
      body.model.startsWith("openpipe:") ||
      body.model.startsWith("openai:") ||
      body.model.startsWith("anthropic:") ||
      body.model.startsWith("gemini:") ||
      openpipe?.cache
    ) {
      if (!this.opCompletionClient) throw new Error("OpenPipe client not set");
      try {
        const response = await this.opCompletionClient.chat.completions.create(body, {
          ...options,
          headers: {
            "op-log-request": openpipe.logRequest ? "true" : "false",
            "op-cache": openpipe?.cache,
            "op-tags": JSON.stringify(getTags(openpipe)),
            "op-criteria": JSON.stringify(openpipe.criteria),
            ...options?.headers,
          },
        });
        return response;
      } catch (e) {
        if (openpipe.fallback?.model) {
          const fallbackBody = { ...body, model: openpipe.fallback.model };
          try {
            const response = await this.fallbackClient.chat.completions.create(fallbackBody, {
              ...options,
              timeout: openpipe.fallback.timeout ?? options?.timeout ?? 10 * 60 * 1000,
            });

            return this._handleResponse(response, fallbackBody, { openpipe }, requestedAt);
          } catch (fallbackError) {
            throw await this._handleResponseError(
              fallbackError,
              fallbackBody,
              { openpipe },
              requestedAt,
            );
          }
        } else {
          throw e;
        }
      }
    }

    try {
      if (this.openaiClient.apiKey === MISSING_OPENAI_API_KEY) {
        throw new OpenAIError(
          "The OPENAI_API_KEY environment variable is missing or empty; either provide it, or instantiate the OpenAI client with an apiKey option, like new OpenAI({ apiKey: 'My API Key' }).",
        );
      }

      // OpenAI does not accept metadata if store is false
      const openAICompatibleBody =
        body.metadata && !body.store ? { ...body, metadata: undefined } : body;

      const response = await super.create(openAICompatibleBody, options);
      return this._handleResponse(response, body, { openpipe }, requestedAt);
    } catch (e: unknown) {
      throw await this._handleResponseError(e, body, { openpipe }, requestedAt);
    }
  }
}
